"use client";

import { LanguageLink } from "@/components/LanguageLink";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Loader2, Menu, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { useLocation, useNavigate } from "react-router-dom";
import { addLanguageToPath, getLanguageFromPath, removeLanguageFromPath } from "@/lib/language";
import logo from "@/assets/images/logo.svg";

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  variant?: "landing" | "dashboard";
}

const useSectionNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  function goToSection(sectionId: string) {
    const currentLang = getLanguageFromPath(location.pathname);
    const basePath = removeLanguageFromPath(location.pathname);

    if(basePath !== '/'){
      const targetPath = `/?section=${sectionId.replaceAll("#", "")}`;
      navigate(addLanguageToPath(targetPath, currentLang));
      return;
    }
    gsap.to(window, { duration: 0.5, scrollTo:{y:sectionId, offsetY:120}});
  }

  return { goToSection };
}

function Header({ variant = "landing", className, ...props }: HeaderProps) {
  const { goToSection } = useSectionNavigation();
  const { isLoaded, isSignedIn } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  const navLinks = {
    landing: [
      { label: t("header.nav.howItWorks"), href: "#how-it-works" },
      { label: t("header.nav.pricing"), href: "#pricing" },
      { label: t("header.nav.faq"), href: "#faq" },
    ],
    dashboard: [
      { label: t("dashboardHeader.nav.createVideo"), href: "/create", active: true },
      { label: t("dashboardHeader.nav.myVideos"), href: "/media" },
      { label: t("dashboardHeader.nav.myAccount"), href: "/account" },
    ],
  };

  const links = navLinks[variant];
  const titleRef = useRef<HTMLImageElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Title animation - runs once on mount
  useGSAP(() => {
    const ctx = gsap.context(() => {
      gsap.set(titleRef.current, { opacity: 0, y: 25 })
      gsap.fromTo(titleRef.current, {opacity: 0, y: 25}, {opacity: 1, y: 0})
    });

    return () => ctx.revert();
  }, []);

  // Mobile menu animation - runs when menu opens
  useGSAP(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      gsap.fromTo(mobileMenuRef.current,
        { x: "100%" },
        { x: 0, duration: 0.3, ease: "power2.out" }
      );
    }
  }, [isMobileMenuOpen]);

  const handleMobileMenuClick = (href: string) => {
    setIsMobileMenuOpen(false);
    goToSection(href);
  };

  return (
    <>
      <header
        className={cn(
          "flex items-center justify-between px-6 py-8 w-full",
          "2k:px-12 2k:py-14",
          "4k:px-12 4k:py-14",
          className
        )}
        {...props}
      >
        {/* Logo */}
        <LanguageLink to="/" className="flex items-center">
          <img 
            ref={titleRef} 
            src={logo} 
            alt={t("header.tagline")}
            className={cn(
              "h-10 md:h-14 2k:h-20 4k:h-32 w-auto object-contain",
              "transition-opacity duration-300"
            )} 
          />
        </LanguageLink>

        {/* Desktop Navigation Links */}
        <nav 
          className={cn(
            "hidden md:flex flex-1 justify-end items-center gap-8 mr-8",
            "2k:gap-12 2k:mr-12",
            "4k:gap-16 4k:mr-16",
          )}
        >
          {links.map((link) => (
            <a
              key={link.href}
              onClick={() => goToSection(link.href)}
              className={cn(
                "font-link cursor-pointer",
                "hover:underline decoration-0 hover:decoration-2 underline-offset-2 hover:underline-offset-6 transition-all duration-100 ease-in-out",
                "4k:hover:decoration-4"
              )}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden hover:bg-gray-100 rounded-md transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="size-8" />
        </button>

        {/* Desktop Login/Dashboard Button */}
        <div className="hidden md:flex items-center gap-2 2k:gap-4 4k:gap-6">
          <Button
            variant="default"
            className={cn(
              "rounded-sm bg-black hover:bg-black/90 font-link text-white px-4 py-5.5",
              "2k:px-6 2k:py-9 2k:rounded-md",
              "4k:px-12 4k:py-14 4k:rounded-xl",
            )}
            asChild
          >
            <LanguageLink to={isLoaded ? "/dashboard" : ""}>
              {isLoaded ? `${isSignedIn ? t("common.dashboard") : t("common.login")}` : <Loader2 className="animate-spin"/>}
            </LanguageLink>
          </Button>
          <LanguageToggle />
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu Panel */}
          <div ref={mobileMenuRef} className="flex flex-col fixed top-0 right-0 bottom-0 w-[30%] min-w-[280px] bg-white z-50 md:hidden shadow-xl">
            {/* Close Button */}
            <div className="flex justify-end p-6">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Navigation Links */}
            <nav className="flex flex-1 flex-col px-6 space-y-6">
              {links.map((link) => (
                <a
                  key={link.href}
                  onClick={() => handleMobileMenuClick(link.href)}
                  className="font-link text-2xl cursor-pointer hover:underline underline-offset-4"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            
            {/* Mobile Login/Dashboard Button */}
            <div className="px-6 pb-8 mt-auto flex flex-col gap-4">
              <LanguageToggle className="w-full py-5 border border-black bg-transparent text-black" />
              <Button
                variant="default"
                className="w-full rounded-sm bg-black hover:bg-black/90 font-link text-white py-5.5"
                asChild
              >
                <LanguageLink to={isLoaded ? "/dashboard" : ""} onClick={() => setIsMobileMenuOpen(false)}>
                  {isLoaded ? `${isSignedIn ? t("common.dashboard") : t("common.login")}` : <Loader2 className="animate-spin"/>}
                </LanguageLink>
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export { Header, useSectionNavigation };
